/**
 * WHERE THE PROFILE PAGE SITS, AND ITS TWO COLUMNS (the owner's live look at real cabinets,
 * 2026-10-01 / design 15).
 *
 * TWO PLACES, ONE PAGE. `/apply` is drawn inside the cabinet's shell for an author the cabinet is
 * open to, and in a frame of its own for an applicant (`ApplyFrame`, src/router.tsx; design 14,
 * F13). The page tells the two apart the way it already does for its header - by whether the router
 * handed it a way into the cabinet (`onOpenCabinet`; see `stepFor` in ../screens/ApplicationScreen).
 *
 *   inside   no frame at all: the shell's column is the page's width and its header and stars are
 *            already there, so the page lines up with every other cabinet screen;
 *   outside  the page brings what the shell would have given it - the field of stars, a centred
 *            column and the MoonX mark over it - so an applicant meets the same place.
 *
 * `ProfileColumns` is the form with the checklist beside it, which stays in view while the form
 * scrolls; `SideCard` is a calm card in that column. Their measurements are in
 * ./profile.module.css.
 */
import { Container } from '@mantine/core';
import { Image as ImageIcon } from '@moonx/ui';
import type { ReactNode } from 'react';
import logoUrl from '../../../assets/moonx-logo.png';
import classes from './profile.module.css';

export interface PageFrameProps {
  /** True when the page is drawn outside the cabinet's shell. */
  readonly outside: boolean;
  /** The centred column's width outside the shell: the form's, or a single message's. */
  readonly size: 'sm' | 'lg';
  readonly children: ReactNode;
}

export function PageFrame({ outside, size, children }: PageFrameProps): ReactNode {
  if (!outside) {
    return <div className={classes.insideCabinet}>{children}</div>;
  }
  return (
    <div className={classes.outside}>
      <div className={classes.sky} aria-hidden="true" />
      <Container size={size} py="xl">
        <img src={logoUrl} alt="MoonX" className={classes.logo} />
        {children}
      </Container>
    </div>
  );
}

/** The form, and the checklist's column beside it (under it on a narrow screen). */
export function ProfileColumns({
  form,
  side,
}: {
  readonly form: ReactNode;
  readonly side: ReactNode;
}): ReactNode {
  return (
    <div className={classes.columns}>
      <div className={classes.form}>{form}</div>
      {/* A `div`, not an `aside`: a complementary landmark inside `main` is one axe refuses. */}
      <div className={classes.aside}>{side}</div>
    </div>
  );
}

/** A calm card in the side column. */
export function SideCard({ children }: { readonly children: ReactNode }): ReactNode {
  return <div className={classes.sideCard}>{children}</div>;
}

/** The photograph section's mark, drawn where the other sections draw their number. */
export function PhotographMark(): ReactNode {
  return <ImageIcon size={13} aria-hidden="true" />;
}
